import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Option, SUPPORTED_LEAGUES } from '../../../../types';
import { useStringsContext } from '../../../../contexts/strings';
import SelectWithValidate, { SelectOptions } from '../../../common/input/select-with-validate';
import { FS_PATH_APP_CONFIGS } from '../../../../constants';
import firebaseApp from '../../../../firebase-app';
import { collection, limit, query, where, getDocs } from 'firebase/firestore';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import { InputWithValidateProps } from '../../../common/input/text-input-with-validate';
import Typography from '@mui/material/Typography';
import { useStaff } from '../../staff/hooks/staff';

const { REACT_APP_DEFAULT_LEAGUE_SEASON } = process.env;

const coachOptionsValidationKey = 'coachOptions';

interface Props extends Omit<InputWithValidateProps, 'onValue'> {
  selectedAbbrv: string;
  onSelectedStaff: (selectedStaff: SelectOptions | null) => void;
  initialSelectedStaff?: SelectOptions;
}

const PollSelectedStaffLeague = ({
  selectedAbbrv,
  onValidation,
  hideValidationError,
  onSelectedStaff,
  initialSelectedStaff,
}: Props) => {
  const { strings } = useStringsContext();
  const { fetchStaff } = useStaff();
  const initSelectedStaff = useRef(false);

  const [staffLoading, setStaffLoading] = useState(false);
  const [staffOptions, setStaffOptions] = useState<SelectOptions>({
    options: [],
    optionsText: [],
  });
  const [selectedStaff, setSelectedStaff] = useState<SelectOptions>({
    options: [],
    optionsText: [],
  });
  const [teamsList, setTeamsList] = useState<SelectOptions>({
    options: [],
    optionsText: [],
  });
  const [selectedTeamAbbrv, setSelectedTeamAbbrv] = useState<string | null>(null);

  useEffect(() => {
    if (initialSelectedStaff && !initSelectedStaff.current) {
      setSelectedStaff(initialSelectedStaff);
      onSelectedStaff(initialSelectedStaff);
      initSelectedStaff.current = true;
    }
  }, [initialSelectedStaff, onSelectedStaff]);

  useEffect(() => {
    onValidation?.(coachOptionsValidationKey, selectedStaff.options.length > 0);
  }, [onValidation, selectedStaff.options.length]);

  useEffect(() => {
    const getStaff = async () => {
      if (selectedTeamAbbrv) {
        setStaffLoading(true);
        try {
          const staff = await fetchStaff(
            selectedTeamAbbrv,
            String(REACT_APP_DEFAULT_LEAGUE_SEASON)
          );
          if (staff) {
            const selectableStaff = staff.filter((member) => member.clId);
            setStaffOptions({
              options: selectableStaff.map(
                (member) =>
                  `${selectedTeamAbbrv}/${String(REACT_APP_DEFAULT_LEAGUE_SEASON)}/${String(member.clId)}`
              ),
              optionsText: selectableStaff.map(
                (member) => member.fullName || member.displayName || String(member.clId)
              ),
            });
          }
        } catch (error) {
          // Do nothing
        }
        setStaffLoading(false);
      }
    };
    getStaff();
  }, [fetchStaff, selectedTeamAbbrv]);

  useEffect(() => {
    const getTeams = async () => {
      const league = selectedAbbrv as keyof typeof SUPPORTED_LEAGUES;
      const colRef = collection(firebaseApp.db, FS_PATH_APP_CONFIGS);
      const docs = await getDocs(
        query(colRef, where('configLeagueName', '==', SUPPORTED_LEAGUES[league]), limit(25))
      );
      const teams = docs.docs.map((doc) => doc.data());
      setTeamsList({
        options: teams.map((team: any) => team.appShortname),
        optionsText: teams.map((team: any) => team.name),
      });
    };
    getTeams();
  }, [selectedAbbrv]);

  const handleAddStaff = useCallback(
    (value: Option | null) => {
      if (selectedStaff.options.includes(String(value))) {
        return;
      }

      const index = staffOptions.options.findIndex((o) => o === String(value));
      const text = staffOptions.optionsText[index];
      const updatedSelectedStaff = {
        options: [...selectedStaff.options, String(value)],
        optionsText: [...selectedStaff.optionsText, `${text} (${selectedTeamAbbrv})`],
      };

      setSelectedStaff(updatedSelectedStaff);
      onSelectedStaff(updatedSelectedStaff);
    },
    [
      onSelectedStaff,
      selectedStaff.options,
      selectedStaff.optionsText,
      selectedTeamAbbrv,
      staffOptions.options,
      staffOptions.optionsText,
    ]
  );

  const handleDeleteStaff = useCallback(
    (index: number) => {
      const updatedSelectedStaff = {
        options: selectedStaff.options.filter((_, i) => i !== index),
        optionsText: selectedStaff.optionsText.filter((_, i) => i !== index),
      };

      setSelectedStaff(updatedSelectedStaff);
      onSelectedStaff(updatedSelectedStaff);
    },
    [onSelectedStaff, selectedStaff.options, selectedStaff.optionsText]
  );

  const handleTeamValue = useCallback((_key: string, value: Option | null) => {
    setSelectedTeamAbbrv(value as string);
  }, []);

  return (
    <>
      <SelectWithValidate
        name="team"
        options={teamsList.options}
        optionsText={teamsList.optionsText}
        value={''}
        onValue={handleTeamValue}
        label={strings.polls.quizSelectTeamLabel}
        hideValidationError={hideValidationError}
        disabled={teamsList.options.length === 0}
      />
      <SelectWithValidate
        name="coach"
        options={staffOptions.options}
        optionsText={staffOptions.optionsText}
        renderValue={() => (
          <Typography color="#000000A0">{strings.polls.quizQuestionSelectMoreLabel}</Typography>
        )}
        onValue={(_, value) => handleAddStaff(value)}
        label={strings.polls.selectCoachesLabel}
        hideValidationError={hideValidationError}
        disabled={staffLoading || staffOptions.options.length === 0}
      />
      {selectedStaff.optionsText.length > 0 && (
        <Grid container spacing={1} sx={{ paddingBottom: '2px' }}>
          {selectedStaff.optionsText.map((member, index) => (
            <Grid key={index} item>
              <Chip label={member} onDelete={() => handleDeleteStaff(index)} />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};

export default PollSelectedStaffLeague;
